import * as React from 'react'
import {useEffect, useMemo, useState} from 'react'
import type {FormattedSegments} from '../google-maps';
import { GOOGLE_MAP_COLORS, makeStopKey} from '../google-maps'
import css from './GoogleMap.less'
import type { Zone} from '../helpers/zone-types';
import {clipPolyToGmapPath} from '../helpers/zone-types'
import usePromise from '../hooks/usePromise'
import LoadingDots from './LoadingDots'
import {ErrorText} from './grid-form'
import {loadGoogleMapsApi} from '../util/load-script'
import {getLatLngForStops} from '../state/address-latlng'
import {STOP_COLORS} from './Stop'
import {getMapDirectionsForSegment} from '../map-directions'
import CheckboxLabel from './CheckboxLabel'
import {convertDistance} from '../distance'
import objectHash from 'object-hash'

export enum MapZoneStyle {
    OUTLINE,
    FILLED,
}

interface GoogleMapProps {
    apiKey?: string;
    segments?: FormattedSegments;
    distanceUnit?: string
    zones?: Zone[]
    zoneStyle?: MapZoneStyle
}

// https://stackoverflow.com/a/23163930/65387
const PIN_SVG_FILLED = "m11 2c-3.9 0-7 3.1-7 7 0 5.3 7 13 7 13 0 0 7-7.8 7-13 0-3.9-3.1-7-7-7z";
// const ARROW_HEAD = "m12.8 20.4l6.4-17.3c0.7-2-1.7-3.5-3.2-2l-5 5-5-5c-1.4-1.5-3.9 0.1-3.1 2.1l6.3 17.2c0.6 1.5 3 1.5 3.6 0z";
// const PATH_COLOR = '#0470B6'

interface GoogleMapOverlay {
    setMap(map: google.maps.Map|null): void
}

type StopMarker = {
    label: string
    markerColor: string
    position: google.maps.LatLngLiteral|null
}

type SegmentMarkers = {
    pathColor: string
    stops: StopMarker[]
}

export default function DynamicGoogleMap({apiKey, segments=[], distanceUnit='MI', zones, zoneStyle}: GoogleMapProps) {
    const apiPromise = usePromise<typeof google.maps,Error>(() => loadGoogleMapsApi(apiKey), [])
    const [containerRef,setContainerRef] = useState<HTMLDivElement|null>(null)
    const [gmap,setGmap] = useState<google.maps.Map|null>(null)
    const [showDirections, setShowDirections] = useState(false)

    const stopHash = useMemo(() => objectHash(segments.map(seg => seg.stops.map(stop => makeStopKey(stop)))),[segments])

    useEffect(() => {
        setShowDirections(false)
    }, [stopHash])

    // TODO: useDebouncedPromise?
    // TODO: use marker lat-lng as input for directions instead of addresses?
    const directions = usePromise(async () => {
        if(!segments?.length) return Promise.reject("No segments")
        if(!showDirections) return Promise.reject("Not requested")
        const directions = await Promise.all(segments.map(s => getMapDirectionsForSegment(s)))
        let distance = 0;
        let duration = 0;
        for(const segDir of directions) {
            if(segDir.routes.length !== 1) {
                throw new Error("Expected exactly 1 route")
            }
            for(const leg of segDir.routes[0].legs) {
                if(leg.distance) {
                    distance += leg.distance.value
                }
                if(leg.duration) {
                    duration += leg.duration.value
                }
            }
        }
        return {
            distance,
            duration,
            directions,
        }
    }, [stopHash,showDirections])

    // const stops = segments?.flatMap(seg => seg.stops.map(stop => formatAddress(stop)))
    // console.log(stops)

    const markerPositions = usePromise(async () => {
        if(!segments?.length) return Promise.reject("No segments")
        const positions = await Promise.allSettled(getLatLngForStops(segments.flatMap(seg => seg.stops)))
        const markers: SegmentMarkers[] = []
        let i=0;
        for(const [segIdx,seg] of segments.entries()) {
            const segMarkers: SegmentMarkers = {
                pathColor: GOOGLE_MAP_COLORS[segIdx%GOOGLE_MAP_COLORS.length],
                stops: [],
            }
            for(const [stopIdx,stop] of seg.stops.entries()) {
                const pos = positions[i]
                const stopMarker: StopMarker = {
                    label: `${seg.number}${stop.letter}`,
                    markerColor:  STOP_COLORS[stop.position] ?? STOP_COLORS['ST'],
                    position: null,
                }
                if(pos.status === 'fulfilled') {
                    const {lat,lng} = pos.value
                    stopMarker.position = {lat,lng}
                }
                segMarkers.stops.push(stopMarker)
                ++i;
            }
            markers.push(segMarkers)
        }
        return markers
    }, [stopHash])

    // logJson(markerPositions)

    useEffect(() => {  // MAP
        if(!apiPromise.value || !containerRef) return

        setGmap(new apiPromise.value.Map(containerRef, {
            // center: {lat: 39.8333333, lng: -98.585522}, // TODO: center at company
            zoom: 12,
            disableDefaultUI: true,
            clickableIcons: false,
            scrollwheel: true,
            gestureHandling: 'cooperative',
            styles: [
                {
                    featureType: "poi",
                    stylers: [
                        { "saturation": -85 }
                    ]
                },
                // {
                //     featureType: "poi",
                //     elementType: "labels",
                //     stylers: [
                //         { visibility: "off" }
                //     ]
                // },
                {
                    featureType: "transit",
                    stylers: [
                        { visibility: "off" }
                    ]
                },
            ]
        }))

        return () => {
            setGmap(null)
        }
    }, [containerRef, apiPromise.value])

    useEffect(() => {  // ZONES
        if(!gmap || !apiPromise.value || !zones?.length) return

        const bounds = new apiPromise.value.LatLngBounds()
        const overlays: GoogleMapOverlay[] = []

        for (const zone of zones) {
            const paths = clipPolyToGmapPath(zone.polygon)
            const gpolyOptions = {
                strokeOpacity: .35,
                paths,
                geodesic: false,
                clickable: false,
                map: gmap,
            }
            if (zoneStyle === MapZoneStyle.FILLED) {
                Object.assign(gpolyOptions, {
                    strokeOpacity: 0.8,
                    fillOpacity: 0.35,
                    fillColor: zone.color,
                    strokeColor: zone.color,
                })
            } else {
                Object.assign(gpolyOptions, {
                    strokeOpacity: 0.90,
                    fillOpacity: 0.20,
                    strokeWeight: 1,
                    fillColor: zone.color,
                    strokeColor: zone.color,
                })
            }
            overlays.push(new apiPromise.value.Polygon(gpolyOptions))

            for(const path of paths) {
                for(const ll of path) {
                    bounds.extend(ll)
                }
            }
        }

        gmap.fitBounds(bounds)

        return () => {
            for(const m of overlays) {
                m.setMap(null)
            }
        }
    }, [apiPromise.value, zones, zoneStyle, gmap])

    useEffect(() => {  // DIRECTIONS
        if(!gmap || !apiPromise.value || !directions.value || !showDirections) return

        const overlays: GoogleMapOverlay[] = []
        for(const [i,result] of directions.value.directions.entries()) {
            const directionsRenderer = new google.maps.DirectionsRenderer({
                map: gmap,
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                    strokeColor: GOOGLE_MAP_COLORS[i%GOOGLE_MAP_COLORS.length],
                    strokeWeight: 6,
                    strokeOpacity: 0.6,
                    clickable: false,
                },
                directions: result,
            })
            overlays.push(directionsRenderer)
        }

        return () => {
            for(const m of overlays) {
                m.setMap(null)
            }
        }
    }, [directions, showDirections, gmap, apiPromise.value]);

    useEffect(() => {  // ARROWS
        if(!gmap || !apiPromise.value || !markerPositions.value?.length || showDirections) return

        const overlays: GoogleMapOverlay[] = []

        for(const seg of markerPositions.value) {
            let lastPos:  google.maps.LatLngLiteral|nil = null
            for(const stop of seg.stops) {
                // logJson(position)

                if(stop.position && lastPos) {
                    overlays.push(new apiPromise.value.Polyline({
                        map: gmap,
                        path: [
                            lastPos,
                            stop.position
                        ],
                        strokeColor: seg.pathColor,
                        strokeWeight: 6,
                        strokeOpacity: 0.6,
                        clickable: false,
                        icons: [{
                            icon: {
                                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                // anchor: new apiPromise.value.Point(11,22),
                                fillColor: seg.pathColor,
                                fillOpacity: 0.6,
                                strokeWeight: 0,
                                strokeOpacity: 0,
                                scale: 8,
                            },
                            offset: '100%'
                        }]
                    }))
                }

                lastPos = stop.position
            }
        }


        return () => {
            for(const m of overlays) {
                m.setMap(null)
            }
        }
    }, [apiPromise.value,gmap,markerPositions,showDirections])

    useEffect(() => {  // MARKERS
        if(!gmap || !apiPromise.value || !markerPositions.value?.length) return

        const bounds = new apiPromise.value.LatLngBounds()
        const overlays: GoogleMapOverlay[] = []
        let posFound = 0


        for(const seg of markerPositions.value) {
            for(const stop of seg.stops) {
                // logJson(position)

                if(stop.position) {
                    overlays.push(new apiPromise.value.Marker({
                        map: gmap,
                        position: stop.position,
                        clickable: false,
                        icon: {
                            path: PIN_SVG_FILLED,
                            anchor: new apiPromise.value.Point(11,22),
                            fillOpacity: 1,
                            fillColor: stop.markerColor,
                            strokeWeight: 3,
                            strokeColor: '#FFF',
                            strokeOpacity: 0.8,
                            scale: 2,
                            labelOrigin: new apiPromise.value.Point(11,9),
                        },
                        label: {
                            text: stop.label,
                            color: 'white',
                            fontSize: '14px',
                            // fontWeight: 'bold',
                            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'
                        }
                    }))
                    // overlays.push(new apiPromise.value.Marker({
                    //     map: gmap,
                    //     position: position,
                    // }))
                    bounds.extend(stop.position)
                    ++posFound
                }
            }
        }

        // apiPromise.value.event.addListenerOnce(gmap, 'zoom_changed', () => {
        //     const zoom = gmap.getZoom()
        //     gmap.setZoom(zoom ? Math.min(zoom,22) : 22)
        // })

        if(posFound) {
            // console.log(posFound)
            gmap.setOptions({
                maxZoom: 18,
            })
            gmap.fitBounds(bounds)
            gmap.setOptions({
                maxZoom: undefined,
            })
        }

        return () => {
            for(const m of overlays) {
                m.setMap(null)
            }
        }
    }, [apiPromise.value,gmap,markerPositions])

    return (
        <div>
            <div ref={setContainerRef} className={css.mapPlaceholder}>
                {!apiPromise.value ? (apiPromise.pending ? <LoadingDots/> : <ErrorText>{__DEV__ && apiPromise.error ? String(apiPromise.error) : "Failed to load Google Map"}</ErrorText>) : null}
            </div>
            {!segments?.length ? null : <div className={css.distanceLine}>
                <span>
                <CheckboxLabel checked={showDirections} onChange={ev => setShowDirections(ev.currentTarget.checked)}>Show Directions</CheckboxLabel>
                </span>
                {!directions.value || !showDirections ? null : <span>
                    <strong>Distance:</strong> {directions.value.distance ? `${convertDistance(directions.value.distance, distanceUnit, 1)} ${distanceUnit.toLowerCase()}` : '—'}, <strong>Duration:</strong> {directions.value.duration ? `${Math.round(directions.value.duration / 60)} mins` : '—'}
                </span>}
            </div>}
        </div>
    )
}



